import { FC, useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { usePrevious } from 'react-use'
import { Button, IconButton, InputAdornment } from '@material-ui/core'
import { TextFieldProps } from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import styled from 'styled-components'
import { ExpandMore } from 'svgIcons'
import { CoverLetterOrderField } from 'generated/graphql'
import TextField from 'components/custom-material-ui/TextField'
import { FormData } from 'screens/CoverLetterScreen/types'
import { useSearchStateContext } from 'screens/CoverLetterScreen/useSearchState'
import CloseIcon from 'svgIcons/Close'
import ListBoxIcon from 'svgIcons/ListBox'
import { isSpamSearchInput } from 'utils/spamKeywordsUtils'
import OrganizationNameFieldMobileMui from './OrganizationNameFieldMobileMui'
import RoleFieldMobileMui from './RoleFieldMobileMui'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: 10,
  },
  formWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 400,
    paddingLeft: 20,
    paddingRight: 20,
    gap: '5px',
    margin: 'auto',
  },
  textFieldWrapper: {
    width: '100%',
  },
  input: {
    fontWeight: 500,
    fontSize: 16,
    color: '#333',
    opacity: 1,
    padding: '10.5px 3px',
  },
  searchButton: {
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    marginTop: '10px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#0082d0',
    },
  },
  fieldIcon: {
    marginLeft: 16,
    height: 16,
    width: 16,
    color: '#333333',
  },
  closeIcon: {
    width: 10,
    height: 10,
  },
  textFieldInput: {
    backgroundColor: 'white',
  },
  field: {
    width: '100%',
    paddingRight: 0,
  },
  dropdownBtn: {
    background: 'none',
    border: 'none',
    padding: '0px',
    color: '#666666',
    fontSize: 12,
    display: 'block',
  },
  organizationWrapper: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '4px',
    width: '100%',
  },
  organizationSubmitBtn: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    height: 39,
    width: 80,
    padding: 'auto',
    '&:hover': {
      backgroundColor: '#0082d0',
    },
  },
  expandMoreArrow: {
    width: 10,
    height: 10,
    marginLeft: 5,
  },
  collapseArrow: {
    width: 10,
    height: 10,
    marginLeft: 5,
    marginTop: 15,
    transform: 'scaleY(-1)',
  },
}))

interface rootProps {
  className?: string
}

export type TShowAutoCompleteType = 'role' | 'organizationName' | ''

interface SubmitButtonProps {
  className?: string
}

interface SearchFormMobileMuiProps {
  submitButtonID?: string
  renderRecommendSearchKeyword?: boolean
  rootProps?: rootProps
  SubmitButtonProps?: SubmitButtonProps
  InputProps?: TextFieldProps['InputProps']
  coverLetterId?: string
}

const SearchFormMobileMui: FC<SearchFormMobileMuiProps> = (props) => {
  const classes = useStyles()
  const router = useRouter()
  const {
    coverLetterId,
    submitButtonID,
    rootProps,
    SubmitButtonProps,
    InputProps: textFieldInputProps,
  } = props

  const {
    organizationName = '',
    role = '',
    keyword = '',
  } = router?.query as Partial<FormData>

  const methods = useForm<FormData>({
    defaultValues: {
      organizationName,
      role,
      keyword,
    },
  })

  const { watch, register, handleSubmit, formState, setValue, reset } = methods

  const { isSubmitting, isDirty } = formState

  const watchKeyword = watch('keyword')

  const { tabState } = useSearchStateContext()

  const prevTabState = usePrevious(tabState)

  // 상세페이지인 경우 coverLetterId props 전달
  const [dropdownOpened, setDropdownOpened] = useState<null | boolean>(
    coverLetterId ? false : null,
  )
  const [showAutoComplete, setShowAutoComplete] =
    useState<TShowAutoCompleteType>('')

  useEffect(() => {
    setDropdownOpened(coverLetterId ? false : null)
  }, [coverLetterId])

  useEffect(() => {
    setValue('organizationName', organizationName)
  }, [organizationName])

  useEffect(() => {
    setValue('role', role)
  }, [role])

  useEffect(() => {
    setValue('keyword', keyword)
  }, [keyword])

  useEffect(() => {
    if (!prevTabState) {
      return
    }
    if (prevTabState !== tabState) {
      reset({ organizationName: '', role: '', keyword: '' })
    }
  }, [tabState])

  const onSubmit: SubmitHandler<FormData> = ({
    organizationName,
    role,
    keyword,
  }: FormData) => {
    if (!isDirty) {
      return
    }

    const inputFields = [organizationName, role, keyword]

    if (isSpamSearchInput(inputFields)) {
      return
    }

    setShowAutoComplete('')

    const nextQuery = {
      ...router.query,
      organizationName: organizationName || '',
      role: role || '',
      keyword: keyword || '',
      page: 1,
      sort: CoverLetterOrderField.PASSED_AT,
    }

    const qs = `?${queryString.stringify(nextQuery)}`

    router
      .push(`/cover-letter/search${qs}`, undefined, { shallow: true })
      .then(() => {
        reset({ organizationName, role, keyword }, { keepDirty: false })
      })
  }

  const handleDropdownBtnClick = () => {
    setDropdownOpened(!dropdownOpened)
  }

  const InputProps = {
    ...textFieldInputProps,
    className: clsx(classes.textFieldInput, textFieldInputProps?.className),
    inputProps: {
      ...textFieldInputProps?.inputProps,
      className: clsx(
        classes.input,
        textFieldInputProps?.inputProps?.className,
      ),
    },
  }

  const registeredKeyword = register('keyword')

  const handleOnResetKeyword = () => {
    setValue('keyword', '', {
      shouldDirty: true,
    })
  }

  return (
    <div className={clsx(classes.root, rootProps?.className)}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classes.formWrapper}
          autoComplete="off"
        >
          <div className={classes.organizationWrapper}>
            <OrganizationNameFieldMobileMui
              className={classes.field}
              InputProps={InputProps}
              showAutoComplete={showAutoComplete}
              setShowAutoComplete={setShowAutoComplete}
            />
            {dropdownOpened === false && (
              <Button
                type="submit"
                disableElevation
                className={classes.organizationSubmitBtn}
                disabled={isSubmitting}
                variant="contained"
                id={submitButtonID}
              >
                검색
              </Button>
            )}
          </div>
          {dropdownOpened !== false && (
            <>
              <RoleFieldMobileMui
                className={classes.field}
                InputProps={InputProps}
                showAutoComplete={showAutoComplete}
                setShowAutoComplete={setShowAutoComplete}
              />
              <StyledSearchInput>
                <TextField
                  inputRef={registeredKeyword.ref}
                  fullWidth
                  {...registeredKeyword}
                  className={classes.field}
                  placeholder="자소서 항목, 내용 등 키워드"
                  type="search"
                  InputProps={{
                    ...InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <ListBoxIcon className={classes.fieldIcon} />
                      </InputAdornment>
                    ),
                    endAdornment: watchKeyword ? (
                      <InputAdornment position="end">
                        <IconButton onClick={handleOnResetKeyword}>
                          <CloseIcon className={classes.closeIcon} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </StyledSearchInput>
              <Button
                type="submit"
                disableElevation
                className={clsx(
                  classes.searchButton,
                  SubmitButtonProps?.className,
                )}
                disabled={isSubmitting}
                variant="contained"
                id={submitButtonID}
              >
                검색
              </Button>
            </>
          )}
          {dropdownOpened !== null && (
            <button
              type="button"
              className={classes.dropdownBtn}
              onClick={handleDropdownBtnClick}
            >
              상세검색 {dropdownOpened === false && '펼치기'}
              {dropdownOpened === true && '닫기'}
              {dropdownOpened === true && (
                <ExpandMore className={classes.collapseArrow} />
              )}
              {dropdownOpened === false && (
                <ExpandMore className={classes.expandMoreArrow} />
              )}
            </button>
          )}
        </form>
      </FormProvider>
    </div>
  )
}

export default SearchFormMobileMui

const StyledSearchInput = styled.div`
  input {
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }
  }
  width: 100%;
`
